import {
  AreaSeries,
  BarSeries,
  CandlestickSeries,
  ColorType,
  HistogramSeries,
  LineSeries,
  createChart,
  type IChartApi,
  type ISeriesApi,
  type UTCTimestamp,
} from "lightweight-charts";
import { useEffect, useRef, useState } from "react";
import type { Bar, ChartType } from "@chartandgo/shared";
import type { Theme } from "../store/workspace.js";
import { formatPrice, formatVolume } from "../lib/format.js";

interface PriceChartProps {
  bars: Bar[];
  chartType: ChartType;
  showVolume: boolean;
  theme: Theme;
  isLoadingEarlier?: boolean;
  onLoadEarlier?: () => void;
}

interface Palette {
  background: string;
  text: string;
  grid: string;
  border: string;
  up: string;
  down: string;
  line: string;
  areaTop: string;
  areaBottom: string;
  volumeUp: string;
  volumeDown: string;
}

const PALETTES: Record<Theme, Palette> = {
  dark: {
    background: "transparent",
    text: "#9aa4b2",
    grid: "rgba(255,255,255,0.05)",
    border: "rgba(255,255,255,0.08)",
    up: "#22c55e",
    down: "#f43f5e",
    line: "#3b82f6",
    areaTop: "rgba(59,130,246,0.35)",
    areaBottom: "rgba(59,130,246,0.02)",
    volumeUp: "rgba(34,197,94,0.5)",
    volumeDown: "rgba(244,63,94,0.5)",
  },
  light: {
    background: "transparent",
    text: "#5b6470",
    grid: "rgba(0,0,0,0.05)",
    border: "rgba(0,0,0,0.08)",
    up: "#16a34a",
    down: "#e11d48",
    line: "#2563eb",
    areaTop: "rgba(37,99,235,0.25)",
    areaBottom: "rgba(37,99,235,0.02)",
    volumeUp: "rgba(22,163,74,0.4)",
    volumeDown: "rgba(225,29,72,0.4)",
  },
};

interface HoverInfo {
  price: number | null;
  open: number | null;
  high: number | null;
  low: number | null;
  close: number | null;
  volume: number | null;
}

export function PriceChart({
  bars,
  chartType,
  showVolume,
  theme,
  isLoadingEarlier = false,
  onLoadEarlier,
}: PriceChartProps) {
  const interactionRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const priceSeriesRef = useRef<ISeriesApi<
    "Candlestick" | "Line" | "Area" | "Bar"
  > | null>(null);
  const volumeSeriesRef = useRef<ISeriesApi<"Histogram"> | null>(null);
  const barsRef = useRef<Bar[]>(bars);
  const loadEarlierRef = useRef(onLoadEarlier);
  const historyGestureRef = useRef(false);
  const renderedFirstTimeRef = useRef<number | null>(null);
  barsRef.current = bars;
  loadEarlierRef.current = onLoadEarlier;

  const [hover, setHover] = useState<HoverInfo | null>(null);

  useEffect(() => {
    const interactionLayer = interactionRef.current;
    if (!interactionLayer) return undefined;

    const preserveChartGesturesButNotVerticalScroll = (event: WheelEvent) => {
      // Chromium reports trackpad pinch as Ctrl+wheel. Let that reach
      // lightweight-charts. Horizontal gestures pan the timeline, while
      // vertical gestures continue scrolling the dashboard.
      const isPinch = event.ctrlKey || event.metaKey;
      const isHorizontal = Math.abs(event.deltaX) > Math.abs(event.deltaY);
      if (isHorizontal && !isPinch) historyGestureRef.current = true;
      if (!isPinch && !isHorizontal) event.stopPropagation();
    };
    interactionLayer.addEventListener(
      "wheel",
      preserveChartGesturesButNotVerticalScroll,
      { capture: true, passive: true },
    );
    return () =>
      interactionLayer.removeEventListener(
        "wheel",
        preserveChartGesturesButNotVerticalScroll,
        true,
      );
  }, []);

  // Create/recreate the chart whenever structural options change.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const palette = PALETTES[theme];
    const chart = createChart(container, {
      layout: {
        background: { type: ColorType.Solid, color: palette.background },
        textColor: palette.text,
        fontFamily:
          "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        attributionLogo: false,
      },
      autoSize: true,
      grid: {
        vertLines: { color: palette.grid },
        horzLines: { color: palette.grid },
      },
      rightPriceScale: { borderColor: palette.border },
      timeScale: {
        borderColor: palette.border,
        timeVisible: true,
        secondsVisible: false,
      },
      crosshair: { mode: 0 },
      handleScale: {
        axisPressedMouseMove: true,
        mouseWheel: true,
        pinch: true,
      },
    });
    chartRef.current = chart;

    const priceSeries = createPriceSeries(chart, chartType, palette);
    priceSeriesRef.current = priceSeries;
    let volumeResizeObserver: ResizeObserver | null = null;

    if (showVolume) {
      const volumeSeries = chart.addSeries(
        HistogramSeries,
        {
          priceFormat: { type: "volume" },
          priceScaleId: "volume",
        },
        1,
      );
      volumeSeriesRef.current = volumeSeries;
      const resizeVolumePane = () => {
        const volumePane = chart.panes()[1];
        if (!volumePane) return;
        volumePane.setHeight(
          Math.max(
            38,
            Math.min(110, Math.round(container.clientHeight * 0.25)),
          ),
        );
      };
      resizeVolumePane();
      volumeResizeObserver = new ResizeObserver(resizeVolumePane);
      volumeResizeObserver.observe(container);
    }

    applyData(priceSeries, volumeSeriesRef.current, barsRef.current, chartType, palette);
    renderedFirstTimeRef.current = barsRef.current[0]?.time ?? null;
    chart.timeScale().fitContent();

    const handleVisibleRangeChange = (
      logicalRange: { from: number; to: number } | null,
    ) => {
      if (!logicalRange || !historyGestureRef.current) return;
      const rangeInfo = priceSeries.barsInLogicalRange(logicalRange);
      if (rangeInfo && rangeInfo.barsBefore < 20) {
        historyGestureRef.current = false;
        loadEarlierRef.current?.();
      }
    };
    chart
      .timeScale()
      .subscribeVisibleLogicalRangeChange(handleVisibleRangeChange);

    chart.subscribeCrosshairMove((param) => {
      if (!param.time || !param.point) {
        setHover(null);
        return;
      }
      const priceData = param.seriesData.get(priceSeries) as
        | Record<string, number>
        | undefined;
      const volumeData = volumeSeriesRef.current
        ? (param.seriesData.get(volumeSeriesRef.current) as
            | { value: number }
            | undefined)
        : undefined;

      if (!priceData) {
        setHover(null);
        return;
      }
      setHover({
        open: priceData.open ?? null,
        high: priceData.high ?? null,
        low: priceData.low ?? null,
        close: priceData.close ?? priceData.value ?? null,
        price: priceData.close ?? priceData.value ?? null,
        volume: volumeData?.value ?? null,
      });
    });

    return () => {
      chart
        .timeScale()
        .unsubscribeVisibleLogicalRangeChange(handleVisibleRangeChange);
      volumeResizeObserver?.disconnect();
      chart.remove();
      chartRef.current = null;
      priceSeriesRef.current = null;
      volumeSeriesRef.current = null;
    };
  }, [theme, chartType, showVolume]);

  // Update data when bars change without tearing down the chart.
  useEffect(() => {
    const priceSeries = priceSeriesRef.current;
    const chart = chartRef.current;
    if (!priceSeries || !chart) return;
    const visibleRange = chart.timeScale().getVisibleRange();
    const visibleLogicalRange = chart.timeScale().getVisibleLogicalRange();
    const previousFirstTime = renderedFirstTimeRef.current;
    const prependedBars =
      previousFirstTime === null
        ? 0
        : bars.findIndex((bar) => bar.time === previousFirstTime);
    applyData(
      priceSeries,
      volumeSeriesRef.current,
      bars,
      chartType,
      PALETTES[theme],
    );
    renderedFirstTimeRef.current = bars[0]?.time ?? null;
    if (visibleLogicalRange && prependedBars > 0) {
      chart.timeScale().setVisibleLogicalRange({
        from: visibleLogicalRange.from + prependedBars,
        to: visibleLogicalRange.to + prependedBars,
      });
    } else if (visibleRange) {
      chart.timeScale().setVisibleRange(visibleRange);
    } else {
      chart.timeScale().fitContent();
    }
  }, [bars, chartType, theme]);

  return (
    <div ref={interactionRef} className="price-chart">
      <div ref={containerRef} className="price-chart__canvas" />
      {isLoadingEarlier && (
        <div className="price-chart__history-status">Loading earlier data…</div>
      )}
      {hover && (
        <div className="price-chart__legend">
          {hover.open != null && (
            <span>
              O <b>{formatPrice(hover.open)}</b>
            </span>
          )}
          {hover.high != null && (
            <span>
              H <b>{formatPrice(hover.high)}</b>
            </span>
          )}
          {hover.low != null && (
            <span>
              L <b>{formatPrice(hover.low)}</b>
            </span>
          )}
          {hover.close != null && (
            <span>
              C <b>{formatPrice(hover.close)}</b>
            </span>
          )}
          {hover.volume != null && (
            <span>
              Vol <b>{formatVolume(hover.volume)}</b>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function createPriceSeries(
  chart: IChartApi,
  chartType: ChartType,
  palette: Palette,
) {
  switch (chartType) {
    case "line":
      return chart.addSeries(LineSeries, {
        color: palette.line,
        lineWidth: 2,
        priceLineVisible: false,
      });
    case "area":
      return chart.addSeries(AreaSeries, {
        lineColor: palette.line,
        topColor: palette.areaTop,
        bottomColor: palette.areaBottom,
        lineWidth: 2,
        priceLineVisible: false,
      });
    case "bar":
      return chart.addSeries(BarSeries, {
        upColor: palette.up,
        downColor: palette.down,
      });
    case "candlestick":
    default:
      return chart.addSeries(CandlestickSeries, {
        upColor: palette.up,
        downColor: palette.down,
        borderUpColor: palette.up,
        borderDownColor: palette.down,
        wickUpColor: palette.up,
        wickDownColor: palette.down,
      });
  }
}

function applyData(
  priceSeries: ISeriesApi<"Candlestick" | "Line" | "Area" | "Bar">,
  volumeSeries: ISeriesApi<"Histogram"> | null,
  bars: Bar[],
  chartType: ChartType,
  palette: Palette,
) {
  if (chartType === "line" || chartType === "area") {
    priceSeries.setData(
      bars.map((b) => ({ time: b.time as UTCTimestamp, value: b.close })),
    );
  } else {
    priceSeries.setData(
      bars.map((b) => ({
        time: b.time as UTCTimestamp,
        open: b.open,
        high: b.high,
        low: b.low,
        close: b.close,
      })),
    );
  }

  if (volumeSeries) {
    volumeSeries.setData(
      bars.map((b) => ({
        time: b.time as UTCTimestamp,
        value: b.volume,
        color: b.close >= b.open ? palette.volumeUp : palette.volumeDown,
      })),
    );
  }
}
